import { computed, getCurrentInstance, ref, toRefs } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import {
  SUB_OBJECT_TYPES_WITH_K8S,
  PRODUCT_TYPES_WITH_OBJECT_TYPES
} from '@/containers/utils/constant';
import {
  convertRawDataToDataChart,
  checkIsCollectionValue
} from '@/containers/components/chart/utils.js';
import { useChart } from '@/composables/useChart.js';

export const useFetch = (searchModel) => {
  const instance = getCurrentInstance();
  const { $http, $alert, $const } = instance.appContext.config.globalProperties;
  const $store = useStore();
  const getObjects = async (params) => {
    try {
      const data = await $http.product.getProductObjects(
        params,
        searchModel.value.productTypeCode,
        true
      );
      if (!data || !data.length) return [];
      return data.map((item) => ({
        ...item,
        value: item.objectName,
        key: item.objectName,
        text: item.objectDisplayName,
        isSelected: false
      }));
    } catch (e) {
      if (e && e.code === 'AccessDenied') {
        $alert.call(instance.proxy, e);
      }
      return [];
    }
  };

  const getK8sObject = async (params) => {
    const response = await $store.dispatch('authStore/checkAuthorizationCluster', params);
    if (response.type === $const.AuthorizationResult.ALLOW) {
      if (!response.responseData || !response.responseData.length) return [];
      return response.responseData.map((item) => ({
        ...item,
        value: item.objectName,
        key: item.objectName,
        text: item.objectDisplayName,
        isSelected: false
      }));
    }
    return [];
  };

  const getMetrics = async (params) => {
    try {
      const response = $http.product.getProductMetricData(
        params,
        searchModel.value.productTypeCode,
        true,
        true
      );
      return response || [];
    } catch (e) {
      if (e && e.code === 'AccessDenied') {
        $alert.call(instance.proxy, e);
      }
      return [];
    }
  };

  return { getObjects, getK8sObject, getMetrics };
};
export const useMetricFunction = (props, inquiryTime) => {
  // Define Refs
  const metricData = ref([]);
  const listIndividuals = ref([]);
  const { selectedTargets, searchModel, metric, collInterval } = toRefs(props);

  // Use Composable
  const { getObjects, getK8sObject, getMetrics } = useFetch(searchModel);
  const { handleDataChart } = useChart();

  // Defined Computed
  const hasSubType = computed(() =>
    PRODUCT_TYPES_WITH_OBJECT_TYPES.includes(searchModel.value.productTypeCode)
  );
  const isKubernet = computed(
    () => searchModel.value.productTypeCode.toLowerCase() === 'kubernetes'
  );

  const isK8sObjectType = computed(
    () => isKubernet.value && SUB_OBJECT_TYPES_WITH_K8S.includes(searchModel.value.subTypeCode)
  );

  const disabledIndividualItem = computed(() => {
    if (!metric.value.isObjectExist || metric.value.isObjectExist === 'N') return true;
    return metric.value.isObjectExist === 'Y' && metric.value.disableObject === 'Y';
  });

  const isGetObject = computed(() => metric.value.isObjectExist === 'Y' && !hasSubType.value);

  const isK8s = computed(() => isK8sObjectType.value && !disabledIndividualItem.value);

  const getObjectList = async () => {
    if (!selectedTargets.value.length) return false;

    const params = {
      metricKeyList: [metric.value.metricKey],
      productIdList: selectedTargets.value.map((item) => item.productSq || item.productId),
      queryStartDt: inquiryTime.value[0],
      queryEndDt: inquiryTime.value[1]
    };
    listIndividuals.value = await getObjects(params);
  };

  const getK8sObjectList = async () => {
    if (!selectedTargets.value.length) return false;

    const params = {
      productIdList: selectedTargets.value.map((e) => e.productSq || e.productId),
      productObjectType: searchModel.value.subTypeCode,
      productSubName: selectedTargets.value.map((e) => e.objectName),
      queryStartDt: inquiryTime.value[0],
      queryEndDt: inquiryTime.value[1]
    };

    const payload = {
      productTypeCode: searchModel.value.productTypeCode,
      params,
      listDataCheckAuth: selectedTargets.value.map((product) => {
        return {
          productResourceId: product.productSq || product.productId,
          productName: product.productName
        };
      })
    };
    listIndividuals.value = await getK8sObject(payload);
  };

  const buildInitialParams = (
    metric,
    searchModel,
    statisticsTypeList,
    startTime,
    endTime,
    interval,
    isSummary = false
  ) => {
    const initialParams = {
      metricDataConditionConsole: {
        metricKey: metric.metricKey,
        objectType: searchModel.subTypeCode || '',
        productResourceInfosConsole: [],
        statisticsPeriod: null,
        statisticsTypeList
      },
      queryStartDt: startTime,
      queryEndDt: endTime
    };
    if (isSummary) return initialParams;
    return { ...initialParams, interval };
  };
  const addK8sDetails = (params, searchModel, selectedTargets, listIndividuals) => {
    params.metricDataConditionConsole.productSubType = searchModel.subTypeCode;
    params.metricDataConditionConsole.productSubName = selectedTargets.map(
      (item) => item.objectName
    );
    params.metricDataConditionConsole.objectType = listIndividuals[0]?.objectType || 'pod';
  };

  const buildObjectList = (selectedIndividualItems, isGetObject, isK8s) => {
    return isGetObject || isK8s ? [...selectedIndividualItems] : [];
  };

  const populateProductResourceInfos = (
    params,
    selectedTargets,
    metric,
    hasSubType,
    objectList,
    isK8s
  ) => {
    selectedTargets.forEach((item) => {
      const target = {
        productResourceId: item.resourceId || item.productResourceId
      };
      if (metric.isObjectExist === 'Y' && hasSubType && item.objectName && !isK8s) {
        target.objectList = [item.objectName];
      } else {
        target.objectList = objectList;
      }
      params.metricDataConditionConsole.productResourceInfosConsole.push(target);
    });
  };
  const convertInterval = ({ statisticsPeriod, interval, isSummary }) => {
    if (isSummary) return statisticsPeriod ?? '';
    return interval;
  };
  const getMetricData = async (
    { selectedIndividualItems = [], interval, statisticsTypeList, startTime, endTime },
    isSummary
  ) => {
    const isEqualTime = moment(startTime).valueOf() === moment(endTime).valueOf();
    const hasIndividual = listIndividuals.value.length > 0 && selectedIndividualItems.length === 0;

    if (hasIndividual || !selectedTargets.value.length || isEqualTime) {
      metricData.value = [];
      return;
    }

    const params = buildInitialParams(
      metric.value,
      searchModel.value,
      statisticsTypeList,
      startTime,
      endTime,
      interval,
      isSummary
    );

    if (isK8s.value) {
      addK8sDetails(params, searchModel.value, selectedTargets.value, listIndividuals.value);
    }

    const objectList = buildObjectList(selectedIndividualItems, isGetObject.value, isK8s.value);

    populateProductResourceInfos(
      params,
      selectedTargets.value,
      metric.value,
      hasSubType.value,
      objectList,
      isK8s.value
    );
    const isCollectionValue = checkIsCollectionValue(
      interval,
      collInterval.value,
      searchModel.value.productTypeCode
    );
    // As regard summary chart not append null value to chart, so it set intervalTS empty
    const data = await getMetrics(params);
    const rawData = data.map((item) => {
      const { perfData, statisticsPeriod } = item;
      const intervalTS = convertInterval({ statisticsPeriod, interval, isSummary });
      item.perfData = handleDataChart(perfData, intervalTS, startTime, endTime, !!isSummary);
      return item;
    });
    metricData.value = convertRawDataToDataChart({
      rawData,
      isCollectionValue,
      hasIndividual: !!listIndividuals.value.length
    });
  };
  return {
    listIndividuals,
    metricData,
    isK8s,
    isGetObject,
    hasSubType,
    getObjectList,
    getK8sObjectList,
    getMetricData
  };
};
